import { useEffect, useState } from 'preact/hooks';
import { VENUE } from '../config/venue';

export interface AtVenueResult {
  /** True once we have a fix and the user is inside the venue radius. */
  atVenue: boolean;
  /** Geolocation request in flight. False after success, denial, or error. */
  loading: boolean;
  /** Set if the browser denied or errored. Experience is never blocked. */
  error: string | null;
}

const DEFAULT: AtVenueResult = { atVenue: false, loading: true, error: null };
const RESOLVED_FALSE: AtVenueResult = { atVenue: false, loading: false, error: null };

let cached: AtVenueResult = DEFAULT;
let inFlight: Promise<AtVenueResult> | null = null;
const listeners = new Set<(r: AtVenueResult) => void>();

function publish(next: AtVenueResult): void {
  cached = next;
  listeners.forEach((fn) => fn(next));
}

/**
 * Great-circle distance in metres between two WGS-84 coordinates.
 * Haversine formula — accurate enough for "within 100m" gating.
 */
function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Prime the venue check. Safe to call multiple times.
 *
 * **Will not raise a permission prompt.** This was tried twice and twice
 * crashed Android Chrome's WebXR session start when fired alongside the
 * camera + AR prompts — Chrome doesn't tolerate three native permission
 * sheets in rapid succession on immersive-ar entry. We now read location
 * only if `Permissions.query` reports `granted` (i.e. the user has
 * authorised this origin via some other path — browser settings, a
 * previous-session prompt, or a future microsite affordance). In every
 * other case we leave `atVenue: false` and let the off-site coaching copy
 * carry the experience. This keeps the AR launch flow at two prompts:
 * camera → AR.
 *
 * If on-site coaching becomes critical for July 4, add a dedicated microsite
 * button that calls `requestVenueGeoOptIn()` below — it's gated on a fresh
 * user gesture far from the WebXR start path.
 */
export function primeAtVenue(): Promise<AtVenueResult> {
  if (inFlight) return inFlight;

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    publish({ atVenue: false, loading: false, error: 'geolocation unavailable' });
    inFlight = Promise.resolve(cached);
    return inFlight;
  }

  inFlight = (async (): Promise<AtVenueResult> => {
    let canRead = false;
    try {
      const status = await (navigator as any).permissions?.query?.({ name: 'geolocation' });
      canRead = status?.state === 'granted';
    } catch {
      canRead = false;
    }

    if (!canRead) {
      // No prompt — default to off-site copy. Resolves immediately.
      publish({ atVenue: false, loading: false, error: null });
      return cached;
    }

    // Permission already granted for this origin: safe to read position.
    // Browser returns it without raising any UI.
    return readPosition();
  })();

  return inFlight;
}

/**
 * Opt-in path for the geolocation prompt. Wire this to a microsite button
 * that's clearly far from the AR launch — e.g. a "See on-site instructions"
 * affordance on the placement overlay's settings flyout. Must be called
 * from a fresh user gesture so the system prompt is allowed to appear.
 *
 * Not currently wired into the UI; provided so the venue copy can be
 * activated without a code change once the affordance is designed.
 */
export function requestVenueGeoOptIn(): Promise<AtVenueResult> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    publish({ atVenue: false, loading: false, error: 'geolocation unavailable' });
    return Promise.resolve(cached);
  }
  inFlight = readPosition();
  return inFlight;
}

function readPosition(): Promise<AtVenueResult> {
  return new Promise<AtVenueResult>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const distance = haversineMeters(
          pos.coords.latitude,
          pos.coords.longitude,
          VENUE.lat,
          VENUE.lng,
        );
        const next: AtVenueResult = {
          atVenue: distance <= VENUE.radiusMeters,
          loading: false,
          error: null,
        };
        publish(next);
        resolve(next);
      },
      (err) => {
        publish({ atVenue: false, loading: false, error: err.message });
        resolve(cached);
      },
      { timeout: 5000, maximumAge: 5 * 60 * 1000, enableHighAccuracy: false },
    );
  });
}

/**
 * Read the cached venue-check result. Subscribes to updates so when the prime
 * call resolves the component re-renders with the new copy.
 *
 * **Does not prompt for permission on its own** — that has to happen via
 * `primeAtVenue()` from the launch flow. Mounting this hook during a live
 * WebXR session must never trigger a system permission sheet, or the very
 * first tap-to-place gets swallowed by the prompt's focus interaction
 * (observed on Android Chrome, reproducible 100% of the time on a cold
 * launch).
 *
 * If nothing has primed yet, returns `atVenue: false` immediately so the
 * placement overlay falls back to the off-site copy — i.e. failing safe
 * toward the generic instruction rather than blocking on a fix.
 */
export function useAtVenue(): AtVenueResult {
  const [state, setState] = useState<AtVenueResult>(
    inFlight ? cached : RESOLVED_FALSE,
  );

  useEffect(() => {
    if (!inFlight) {
      // Nobody primed — emit off-site default and don't prompt.
      return;
    }
    setState(cached);
    const fn = (r: AtVenueResult) => setState(r);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  return state;
}
